import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap"
import Filters from "components/Common/Filters"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import TableShimmer from "components/Shared/TableShimmer"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { getBusinessTaxReports } from "store/actions/reportsAction"
import Paginations from "components/Common/Paginations"
import { getAllBranchs } from "store/actions/usersAction"
import { groupByBusinessTaxReport } from "components/Common/constants"
import { getCountryList } from "store/actions/countriesAction"
import { getAllRegionList } from "store/actions/regionAction"
import { getAllCityList } from "store/actions/cityAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import { getBranchCountryList } from "store/actions/commonAction"

const BusinessTaxReports = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Business Tax Reports | Sign Burger")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [isApiCalled, setIsApiCalled] = useState(false)

  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    businessTaxReports,
  } = useSelector(state => state.Reports)
  const { branchsList, countryList } = useSelector(state => state.Common)

  const [selectedFilters, setSelectedFilters] = useState({
    branchId: [],
    groupBy: "",
    countryId: [],
    regionId: [],
    cityId: [],
  })
  const [dateRange, setDateRange] = useState({})
  const { regionList } = useSelector(state => state.Region)
  const { cityList } = useSelector(state => state.City)

  const flatpickrRef = useRef(null)

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      16,
      "report_1",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
    })
  }, [userRole])

  useEffect(() => {
    fetchBusinessTaxReports()
    fetchBranchCountryList()
  }, [])

  const fetchBranchCountryList = () => {
    dispatch(getBranchCountryList())
  }

  const fetchRegionList = countryId => {
    dispatch(getAllRegionList(countryId))
  }

  const fetchCityList = (countryId, regionId) => {
    const payload = {
      countryId,
      regionId,
    }
    dispatch(getAllCityList(payload))
  }

  const fetchBusinessTaxReports = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      branchId: [],
      cityId: [],
      regionId: [],
      countryId: [],
      groupBy: "",
      dateRange: {},
    }
    dispatch(getBusinessTaxReports(payload))
    setIsApiCalled(true)
  }

  const filters = [
    {
      name: "branchId",
      type: "multiSelect",
      options: [
        ...branchsList.map(branch => ({
          label: language === "sa" ? branch.nameAr : branch.name,
          value: branch._id,
        })),
      ],
      placeholder: t("Select Branch"),
    },
    {
      name: "groupBy",
      type: "singleSelect",
      options: [
        ...groupByBusinessTaxReport.map(item => ({
          label: t(item.label),
          value: item.value,
        })),
      ],
      placeholder: t("Select Group By"),
    },
    {
      name: "countryId",
      type: "singleSelect",
      options: [
        ...countryList.map(item => ({
          label: language === "sa" ? item.nameAr : item.name,
          value: item._id,
        })),
      ],
      placeholder: t("Select Country"),
    },
  ]
  // ADD REGION DROPDOWN
  if (Object.keys(selectedFilters.countryId).length > 0) {
    filters.push({
      name: "regionId",
      type: "singleSelect",
      options: regionList.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
      placeholder: t("Select Region"),
    })
  }

  // ADD CITY DROPDOWN
  if (Object.keys(selectedFilters.regionId).length > 0) {
    filters.push({
      name: "cityId",
      type: "singleSelect",
      options: cityList.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
      placeholder: t("Select City"),
    })
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleFilterChange = (selectedValue, filterName) => {
    const value = selectedValue.value

    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
    if (filterName === "countryId") {
      // GET REGION LIST
      fetchRegionList(value)
      setSelectedFilters(prev => ({
        ...prev,
        regionId: [],
        cityId: [],
      }))
    } else if (filterName === "regionId") {
      // GET CITIES LIST
      const { countryId } = selectedFilters
      fetchCityList(countryId.value, value)
      setSelectedFilters(prev => ({
        ...prev,
        cityId: [],
      }))
    }
  }

  const handleSearch = () => {
    const { branchId, groupBy, countryId, regionId, cityId } = selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const groupByValue = groupBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      branchId: branchIds,
      cityId: cityValue,
      regionId: regionValue,
      countryId: countryValue,
      groupBy: groupByValue,
      dateRange: {
        min: dateRange.min,
        max: dateRange.max,
      },
    }
    dispatch(getBusinessTaxReports(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/business-tax-reports?page=${1}`)
        }
      })
      .catch(error => {})
  }

  const handleReset = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      branchId: [],
      cityId: [],
      regionId: [],
      countryId: [],
      groupBy: "",
      dateRange: {},
    }
    dispatch(getBusinessTaxReports(payload))
    navigate(`/business-tax-reports?page=${1}`)
    setSelectedFilters({
      branchId: [],
      groupBy: "",
      cityId: [],
      regionId: [],
      countryId: [],
    })
    setDateRange({})
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.clear()
    }
  }

  const onPageChange = page => {
    const { branchId, groupBy, countryId, regionId, cityId } = selectedFilters
    const branchIds = branchId?.map(item => item.value)
    const groupByValue = groupBy.value || ""

    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      branchId: branchIds,
      cityId: cityValue,
      regionId: regionValue,
      countryId: countryValue,
      groupBy: groupByValue,
      dateRange: {
        min: dateRange.min,
        max: dateRange.max,
      },
    }
    dispatch(getBusinessTaxReports(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/business-tax-reports?page=${page}`)
        }
      })
      .catch(error => {})
  }

  const exportToCSV = async () => {
    const { branchId, groupBy, countryId, regionId, cityId } = selectedFilters
    const branchIds = branchId?.map(item => item.value)
    const groupByValue = groupBy.value || ""

    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const payload = {
      type: "REPORT-TAX",
      exportType: "csv",
      branchId: branchIds,
      keyword: "", // STATIC
      dateRange: {
        min: dateRange.min,
        max: dateRange.max,
      },
      groupBy: groupByValue,
      cityId: cityValue,
      regionId: regionValue,
      countryId: countryValue,
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Business Tax Reports"))
    }
  }

  const handleExcelDownload = async () => {
    const { branchId, groupBy, countryId, regionId, cityId } = selectedFilters
    const branchIds = branchId?.map(item => item.value)
    const groupByValue = groupBy.value || ""

    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const payload = {
      type: "REPORT-TAX",
      exportType: "xlsx",
      branchId: branchIds,
      keyword: "", // STATIC
      dateRange: {
        min: dateRange.min,
        max: dateRange.max,
      },
      groupBy: groupByValue,
      cityId: cityValue,
      regionId: regionValue,
      countryId: countryValue,
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Business Tax Reports"))
    }
  }

  const calculateTotal = property => {
    if (!businessTaxReports || !Array.isArray(businessTaxReports)) {
      return 0
    }
    const total = businessTaxReports.reduce((total, currentItem) => {
      const value = currentItem[property] || 0
      return total + value
    }, 0)
    return total?.toFixed(2)
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Business Tax Reports")}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                isExportData={loading ? false : true}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  hideSearch={true}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  isRangeDate={true}
                  dateRange={dateRange}
                  flatpickrRef={flatpickrRef}
                  setDateRange={setDateRange}
                  colSize={4}
                />
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : businessTaxReports.length === 0 ? (
                    <p className="text-center my-2">
                      {t("No business tax report found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light">
                        <tr>
                          <th>{t("Tax")}</th>
                          {selectedFilters.groupBy.value === "order" && (
                            <th>{t("Invoice No")}</th>
                          )}
                          {selectedFilters.groupBy.value === "branch" && (
                            <th>{t("Branch Name")}</th>
                          )}
                          <th>{t("Total Amount")}</th>
                          <th>{t("Total Count")}</th>
                          <th>{t("Tax Amount")}</th>
                          <th>{t("Amount Without Tax")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {businessTaxReports.map(
                          (
                            {
                              tax,
                              invoiceNo,
                              branchName,
                              totalAmount,
                              totalCount,
                              taxAmount,
                              amountWithoutTax,
                              branchNameAr,
                              countryCode,
                            },
                            index
                          ) => (
                            <tr key={index}>
                              <td>{tax}</td>
                              {selectedFilters.groupBy.value === "order" && (
                                <td>{invoiceNo}</td>
                              )}
                              {selectedFilters.groupBy.value === "branch" && (
                                <td>
                                  {language === "sa"
                                    ? branchNameAr
                                    : branchName}
                                </td>
                              )}
                              <td>
                                {totalAmount?.toFixed(2)}{" "}
                                {t(
                                  countryList.find(
                                    c => c.countryCode === countryCode
                                  )?.currency || ""
                                )}
                              </td>
                              <td>{totalCount?.toFixed(2)}</td>
                              <td>
                                {taxAmount?.toFixed(2)}{" "}
                                {t(
                                  countryList.find(
                                    c => c.countryCode === countryCode
                                  )?.currency || ""
                                )}
                              </td>
                              <td>
                                {amountWithoutTax?.toFixed(2)}{" "}
                                {t(
                                  countryList.find(
                                    c => c.countryCode === countryCode
                                  )?.currency || ""
                                )}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                      {/* <tfoot>
                        <tr>
                          <td>Totals</td>
                          {["order", "branch"].includes(
                            selectedFilters?.groupBy?.value
                          ) && <td></td>}
                          <td>{calculateTotal("totalAmount")} SAR</td>
                          <td>{calculateTotal("totalCount")} </td>
                          <td>{calculateTotal("taxAmount")} SAR</td>
                          <td>{calculateTotal("amountWithoutTax")} SAR</td>
                        </tr>
                      </tfoot> */}
                    </Table>
                  )}
                </div>

                {!loading && businessTaxReports.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={businessTaxReports}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default BusinessTaxReports
